import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { FeedPost } from '../../services-mock/mock-feed.service';
import { MockFeedService } from '../../services-mock/mock-feed.service';
import { AuthService } from '../../services/auth.service';

interface Comment {
  id: string;
  author: {
    name: string;
    initials?: string;
    avatar?: string;
  };
  text: string;
  date: Date;
}

@Component({
  selector: 'app-post',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, ButtonModule, AvatarModule],
  template: `
    <div class="post-container">
      <div class="post-header">
        <div class="post-author">
          @if (post.author.avatar) {
          <p-avatar
            [image]="post.author.avatar"
            shape="circle"
            size="normal"
          ></p-avatar>
          } @else {
          <p-avatar
            [label]="post.author.initials || 'U'"
            shape="circle"
            size="normal"
          ></p-avatar>
          }
          <div class="author-info">
            <div 
              class="author-name clickable" 
              (click)="navigateToAuthor()"
            >
              {{ post.author.name }}
            </div>
            <div class="post-date">{{ formattedDate }}</div>
          </div>
        </div>
        <button
          *ngIf="!isCurrentUser"
          pButton
          type="button"
          class="follow-button"
          [label]="isFollowing ? 'Unfollow' : 'Follow'"
          [icon]="isFollowing ? 'pi pi-times' : 'pi pi-wifi'"
          (click)="toggleFollow()"
        ></button>
      </div>

      <div class="post-categories" *ngIf="post.categories.length > 0">
        <span
          *ngFor="let category of post.categories"
          class="category-tag"
        >
          {{ category }}
        </span>
      </div>

      <div class="post-content">
        <p [class.expanded]="isExpanded">
          {{ post.content }}
        </p>
        <button
          *ngIf="post.content.length > 150"
          class="see-more-link"
          (click)="toggleExpand()"
        >
          {{ isExpanded ? 'See less' : 'See more' }}
        </button>
      </div>

      <div class="post-media" *ngIf="post.mediaUrl">
        <div class="media-container" (click)="playMedia()">
          <img [src]="post.mediaUrl" [alt]="post.author.name + ' post'" />
          <div class="play-button">
            <i class="pi pi-play"></i>
          </div>
        </div>
      </div>

      <div class="post-engagement">
        <div class="engagement-left">
          <button
            type="button"
            class="likes-button"
            [class.liked]="post.isLiked"
            (click)="toggleLike()"
          >
            <i class="pi pi-thumbs-up"></i>
            <span>{{ post.likes }}</span>
          </button>
        </div>
        <div class="engagement-right">
          <button
            type="button"
            class="comments-button"
            (click)="toggleComments()"
          >
            <i class="pi pi-comment"></i>
            <span>{{ post.comments }}</span>
          </button>
        </div>
      </div>

      <div class="comments-section" *ngIf="showCommentsSection">
        <div class="comments-list" *ngIf="comments.length > 0">
          <div class="comment-item" *ngFor="let comment of comments">
            <div class="comment-author">
              <p-avatar
                [label]="comment.author.initials || 'U'"
                shape="circle"
                size="normal"
              ></p-avatar>
              <div class="comment-content">
                <div class="comment-author-name">{{ comment.author.name }}</div>
                <div class="comment-text">{{ comment.text }}</div>
                <div class="comment-date">{{ formatCommentDate(comment.date) }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="add-comment">
          <div class="comment-input-container">
            <p-avatar
              [label]="currentUserInitials"
              shape="circle"
              size="normal"
            ></p-avatar>
            <input
              type="text"
              class="comment-input"
              placeholder="Write a comment..."
              [(ngModel)]="newCommentText"
              (keyup.enter)="addComment()"
            />
            <button
              type="button"
              class="comment-submit-button"
              (click)="addComment()"
              [disabled]="!newCommentText.trim()"
            >
              <i class="pi pi-send"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .post-container {
        background-color: #ffffff;
        border-radius: 8px;
        padding: 1.5rem;
        margin-bottom: 1rem;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .post-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 1rem;
      }

      .post-author {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }

      .author-info {
        display: flex;
        flex-direction: column;
      }

      .author-name {
        font-weight: 600;
        font-size: 0.95rem;
        color: #000000;
        margin-bottom: 0.25rem;
      }
      .author-name.clickable {
        cursor: pointer;
        color: #0066cc;
        transition: color 0.2s ease;
      }
      .author-name.clickable:hover {
        color: #0052a3;
        text-decoration: underline;
      }

      .post-date {
        font-size: 0.85rem;
        color: #666666;
      }

      .follow-button {
        font-size: 0.9rem;
        padding: 0.5rem 1rem;
      }

      .post-categories {
        display: flex;
        gap: 0.5rem;
        margin-bottom: 1rem;
      }

      .category-tag {
        background-color: #e5e5e5;
        color: #333333;
        padding: 0.25rem 0.75rem;
        border-radius: 20px;
        font-size: 0.85rem;
      }

      .post-content {
        margin-bottom: 1rem;
      }

      .post-content p {
        color: #333333;
        line-height: 1.6;
        margin: 0;
      }

      .post-content p:not(.expanded) {
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .see-more-link {
        background: none;
        border: none;
        color: #0066cc;
        cursor: pointer;
        padding: 0;
        margin-top: 0.5rem;
        font-size: 0.9rem;
        text-decoration: none;
      }

      .see-more-link:hover {
        text-decoration: underline;
      }

      .post-media {
        margin-bottom: 1rem;
      }

      .media-container {
        position: relative;
        width: 100%;
        border-radius: 8px;
        overflow: hidden;
        cursor: pointer;
      }

      .media-container img {
        width: 100%;
        height: auto;
        display: block;
      }

      .play-button {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 60px;
        height: 60px;
        background-color: rgba(255, 255, 255, 0.9);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background-color 0.2s ease;
      }

      .play-button:hover {
        background-color: rgba(255, 255, 255, 1);
      }

      .play-button i {
        font-size: 1.5rem;
        color: #333333;
        margin-left: 3px;
      }

      .post-engagement {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-top: 1rem;
        border-top: 1px solid #e5e5e5;
      }

      .engagement-left {
        display: flex;
        align-items: center;
      }

      .likes-button {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: #0066cc;
        font-size: 0.9rem;
        font-weight: 500;
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 0.5rem;
        border-radius: 4px;
        transition: background-color 0.2s ease;
      }

      .likes-button:hover {
        background-color: #f5f5f5;
      }

      .likes-button.liked {
        color: #0066cc;
      }

      .likes-button i {
        font-size: 1rem;
      }

      .engagement-right {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .comments-button {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: #666666;
        font-size: 0.9rem;
        font-weight: 500;
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 0.5rem;
        border-radius: 4px;
        transition: background-color 0.2s ease;
      }

      .comments-button:hover {
        background-color: #f5f5f5;
      }

      .comments-button i {
        font-size: 1rem;
      }

      .comments-section {
        margin-top: 1rem;
        padding-top: 1rem;
        border-top: 1px solid #e5e5e5;
      }

      .comments-list {
        margin-bottom: 1rem;
      }

      .comment-item {
        margin-bottom: 1rem;
      }

      .comment-author {
        display: flex;
        gap: 0.75rem;
      }

      .comment-content {
        flex: 1;
      }

      .comment-author-name {
        font-weight: 600;
        font-size: 0.9rem;
        color: #000000;
        margin-bottom: 0.25rem;
      }

      .comment-text {
        font-size: 0.9rem;
        color: #333333;
        line-height: 1.5;
        margin-bottom: 0.25rem;
      }

      .comment-date {
        font-size: 0.8rem;
        color: #999999;
      }

      .add-comment {
        margin-top: 1rem;
        padding-top: 1rem;
        border-top: 1px solid #e5e5e5;
      }

      .comment-input-container {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }

      .comment-input {
        flex: 1;
        padding: 0.75rem;
        border: 1px solid #e5e5e5;
        border-radius: 20px;
        font-size: 0.9rem;
        outline: none;
      }

      .comment-input:focus {
        border-color: #0066cc;
      }

      .comment-submit-button {
        background: #0066cc;
        color: #ffffff;
        border: none;
        border-radius: 50%;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background-color 0.2s ease;
      }

      .comment-submit-button:hover:not(:disabled) {
        background: #0052a3;
      }

      .comment-submit-button:disabled {
        background: #cccccc;
        cursor: not-allowed;
      }

      .comment-submit-button i {
        font-size: 0.9rem;
      }

      :host ::ng-deep {
        .p-button {
          border: none;
          box-shadow: none;
        }

        .p-button:focus {
          box-shadow: none;
        }

        .p-avatar {
          width: 40px;
          height: 40px;
        }
      }
    `,
  ],
})
export class PostComponent {
  @Input() post!: FeedPost;
  isExpanded = false;
  showCommentsSection = false;
  comments: Comment[] = [];
  newCommentText = '';
  currentUserInitials = 'U';
  isFollowing = false;

  private feedService = inject(MockFeedService);
  private authService = inject(AuthService);
  private router = inject(Router);

  constructor() {
    this.currentUserInitials = this.authService.getUserInitials();
  }

  navigateToAuthor() {
    if (this.post.author.id) {
      // Navigate to user profile
      this.router.navigate(['/profile', this.post.author.id]);
    } else {
      // Business/organization post - navigate to organization page
      // Use organizationId if available, otherwise derive from name
      const orgId = this.post.author.organizationId || this.getOrganizationIdFromName(this.post.author.name);
      if (orgId) {
        this.router.navigate(['/organization', orgId]);
      }
    }
  }

  private getOrganizationIdFromName(name: string): string {
    // Convert business name to organization ID format
    // e.g., "Summit Construction Group" -> "org-summit-construction"
    return 'org-' + name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/\b(group|inc|llc|corp|corporation|co|company|ltd|limited)\b/g, '')
      .replace(/\s+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  get isCurrentUser(): boolean {
    const profile = this.authService.getCurrentProfile();
    if (!profile) return false;
    
    const currentUserName = `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
    if (!currentUserName) {
      // Fallback to username if no first/last name
      return profile.username === this.post.author.name;
    }
    
    return currentUserName === this.post.author.name;
  }

  get formattedDate(): string {
    const date = this.post.date;
    const month = date.toLocaleString('default', { month: 'short' });
    const day = date.getDate();
    const year = date.getFullYear();
    const time = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${month} ${day}, ${year} • ${time}`;
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
  }

  toggleLike() {
    this.feedService.likePost(this.post.id).subscribe(() => {
      // The service updates the post in place
    });
  }

  toggleFollow() {
    this.isFollowing = !this.isFollowing;
    // In a real app, this would call a service to follow/unfollow the author
    console.log(
      this.isFollowing ? 'Following' : 'Unfollowing',
      this.post.author.name
    );
  }

  toggleComments() {
    this.showCommentsSection = !this.showCommentsSection;
    if (this.showCommentsSection && this.comments.length === 0) {
      this.loadComments();
    }
  }

  loadComments() {
    // Mock comments - in real app, this would come from the service
    this.comments = [
      {
        id: '1',
        author: {
          name: 'Sarah Johnson',
          initials: 'SJ',
        },
        text: 'This looks amazing! Can\'t wait to see it in action.',
        date: new Date('2025-01-01T10:00:00'),
      },
      {
        id: '2',
        author: {
          name: 'Mike Chen',
          initials: 'MC',
        },
        text: 'Great innovation! How does this compare to existing solutions?',
        date: new Date('2025-01-01T10:30:00'),
      },
      {
        id: '3',
        author: {
          name: 'Emily Davis',
          initials: 'ED',
        },
        text: 'We\'ve been looking for something like this. When will it be available?',
        date: new Date('2025-01-01T11:00:00'),
      },
      {
        id: '4',
        author: {
          name: 'David Wilson',
          initials: 'DW',
        },
        text: 'Impressive work! The analytics features sound particularly useful.',
        date: new Date('2025-01-01T11:15:00'),
      },
    ];
  }

  addComment() {
    if (!this.newCommentText.trim()) {
      return;
    }

    const profile = this.authService.getCurrentProfile();
    const userName = profile
      ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || profile.username || 'You'
      : 'You';

    const newComment: Comment = {
      id: Date.now().toString(),
      author: {
        name: userName,
        initials: this.currentUserInitials,
      },
      text: this.newCommentText.trim(),
      date: new Date(),
    };

    this.comments.push(newComment);
    this.newCommentText = '';
    this.post.comments++;
  }

  formatCommentDate(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });
    }
  }

  playMedia() {
    // TODO: Implement media playback functionality
    console.log('Play media for post:', this.post.id, this.post.mediaUrl);
  }
}

